import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Star, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const useWindowWidth = () => {

  const [width, setWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1200));
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler, { passive: true });
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
};

const SalonCarousel = ({ initialData }) => {
  const { t } = useTranslation('home');
  const [salons, setSalons] = useState(() => initialData ?? []);
  const [currentIndex, setCurrentIndex] = useState(0);
  // Treat an empty initialData array the same as no initialData: the home
  // aggregate may have been served from a stale cache before the ad was
  // purchased, so we must do an immediate fresh fetch to pick it up.
  const [loading, setLoading] = useState(!initialData?.length);
  const navigate = useNavigate();
  const windowWidth = useWindowWidth();

  // Responsive columns driven by real JS width — no CSS interpolation bugs
  const cols = windowWidth <= 600 ? 1 : windowWidth <= 900 ? 2 : 4;
  const gapPx = 20;
  const itemWidth = `calc(${100 / cols}% - ${(gapPx * (cols - 1)) / cols}px)`;
  const trackTransform = `translateX(-${currentIndex * (100 / cols)}%)`;

  useEffect(() => {
    // Skip immediate fetch only when home already provided non-empty data —
    // an empty array means the aggregate was stale (no active ads at cache
    // build time), so we still need to check the live endpoint.
    if (!initialData?.length) fetchFeaturedSalons();
    const interval = setInterval(fetchFeaturedSalons, 5 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialData?.length is checked once at mount to decide whether to skip the first fetch; not a re-trigger condition for this effect's recurring poll
  }, []);
  useEffect(() => {
    if (salons.length > 0) setCurrentIndex((i) => Math.min(i, salons.length - 1));
  }, [cols, salons.length]);

  useEffect(() => {
    if (salons.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % salons.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [salons.length]);

  const fetchFeaturedSalons = async () => {
    try {
      const url = `${import.meta.env.VITE_REACT_APP_API_URL}/api/cities/featured-salons`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.success && data.data.length > 0) {
        setSalons(data.data);
      } else {
        setSalons([]);
      }
    } catch (err) {
      console.error("Failed to fetch featured salons:", err);
    } finally {
      setLoading(false);
    }
  };

  const prev = () => setCurrentIndex((p) => (p - 1 + salons.length) % salons.length);
  const next = () => setCurrentIndex((p) => (p + 1) % salons.length);

  if (loading) {
    return (
      <section style={{ padding: "48px 0", background: "#ffffff" }}>
        <div style={{ textAlign: "center", color: "#a346d5", fontSize: "13px", fontWeight: 500 }}>
          {t('carousel_loading')}
        </div>
      </section>
    );
  }

  if (salons.length === 0) return null;

  return (
    <section style={{
      padding: "64px 0",
      background: "#ffffff",
      position: "relative",
      overflow: "hidden",
    }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 32px", position: "relative" }}>

        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: "36px" }}>
          <div>
            <p style={{
              fontSize: "11px", fontWeight: 700, letterSpacing: "0.18em",
              textTransform: "uppercase", color: "#a346d5", marginBottom: "8px",
            }}>
              {t('carousel_eyebrow')}
            </p>
            <h2 style={{
              fontSize: "clamp(20px, 3.5vw, 32px)", fontWeight: 800,
              color: "#0f0f14", lineHeight: 1.2, margin: 0, letterSpacing: "-0.02em",
            }}>
              {t('carousel_heading')}
            </h2>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={prev}
              style={{
                width: "42px", height: "42px", borderRadius: "9999px",
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "#ffffff",
                border: "1px solid #ebebef",
                color: "#0f0f14", cursor: "pointer",
                boxShadow: "0 1px 3px rgba(13,22,25,0.04)",
                transition: "all 0.2s cubic-bezier(0.22, 1, 0.36, 1)",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#f7f7f8"; e.currentTarget.style.borderColor = "#dcdce2"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#ffffff"; e.currentTarget.style.borderColor = "#ebebef"; }}
            >
              <ChevronLeft style={{ width: 18, height: 18 }} />
            </button>
            <button
              onClick={next}
              style={{
                width: "42px", height: "42px", borderRadius: "9999px",
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "linear-gradient(90deg, #ef6997 0%, #e85cba 50%, #b84cdc 100%)",
                border: "none", color: "#fff", cursor: "pointer",
                boxShadow: "0 4px 16px rgba(184,76,220,0.35)",
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.07)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(184,76,220,0.50)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(184,76,220,0.35)"; }}
            >
              <ChevronRight style={{ width: 18, height: 18 }} />
            </button>
          </div>
        </div>

        <div style={{ overflow: "hidden" }}>
          <div
            style={{
              display: "flex", gap: `${gapPx}px`,
              transition: "transform 0.7s cubic-bezier(0.25,0.46,0.45,0.94)",
              transform: trackTransform,
            }}
          >
            {salons.map((salon, idx) => (
              <div
                key={`${salon.id}-${idx}`}
                style={{ flexShrink: 0, width: itemWidth, cursor: "pointer" }}
                onClick={() => navigate(`/salon/${salon.id}`)}
              >
                <div
                  className="salon-card"
                  style={{
                    background: "#ffffff",
                    borderRadius: "18px",
                    overflow: "hidden",
                    border: "1px solid #ebebef",
                    boxShadow: "0 1px 3px rgba(13,22,25,0.04), 0 12px 32px rgba(13,22,25,0.06)",
                    transition: "transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s cubic-bezier(0.22, 1, 0.36, 1)",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 4px 8px rgba(13,22,25,0.06), 0 16px 40px rgba(13,22,25,0.1)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 1px 3px rgba(13,22,25,0.04), 0 12px 32px rgba(13,22,25,0.06)";
                  }}
                >
                  <div style={{ position: "relative", overflow: "hidden", height: 180 }}>
                    <img
                      src={salon.image_url || "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400"}
                      alt={salon.salon_name}
                      style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.5s ease", display: "block" }}
                    />
                                        {salon.review_count > 0 && (

                      <div style={{
                        position: "absolute", top: "10px", right: "10px",
                        display: "flex", alignItems: "center", gap: "4px",
                        padding: "4px 10px", borderRadius: "99px",
                        background: "rgba(255,255,255,0.92)",
                        boxShadow: "0 2px 8px rgba(13,22,25,0.12)",
                      }}>
                        <Star style={{ width: 11, height: 11, fill: "#f5c842", color: "#f5c842" }} />
                        <span style={{ fontSize: "11px", fontWeight: 700, color: "#0f0f14" }}>
                          {parseFloat(salon.avg_rating).toFixed(1)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div style={{ padding: "14px 16px 18px" }}>
                    <h3 style={{
                      fontSize: "14px", fontWeight: 700, color: "#0f0f14",
                      margin: "0 0 5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {salon.salon_name}
                    </h3>
                    <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "5px" }}>
                      <MapPin style={{ width: 11, height: 11, flexShrink: 0, color: "#a346d5" }} />
                      <p style={{
                        fontSize: "11px", color: "#5b5b6b", margin: 0,
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>
                        {salon.salon_city}
                      </p>
                    </div>
                    {salon.review_count > 0 && (
                      <p style={{ fontSize: "11px", fontWeight: 600, color: "#a346d5", margin: 0 }}>
                        {t('carousel_reviews', { count: salon.review_count })}
                      </p>
                    )}
                    <div style={{
                      marginTop: "12px", height: "2px", width: "28px", borderRadius: "99px",
                      background: "linear-gradient(90deg, #ef6997 0%, #e85cba 50%, #b84cdc 100%)",
                    }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SalonCarousel;